/**
 * PWA Service Worker Registration & Management
 *
 * Handles service worker registration, updates, and lifecycle events.
 * Provides utilities for PWA install prompts and offline detection.
 */

// Service worker registration options
interface RegistrationOptions {
  onUpdate?: (registration: ServiceWorkerRegistration) => void;
  onSuccess?: (registration: ServiceWorkerRegistration) => void;
  onOffline?: () => void;
  onOnline?: () => void;
}

// Service worker update status
export type UpdateStatus = 'checking' | 'available' | 'installing' | 'activated' | 'error';

/**
 * Register the service worker
 */
export async function registerServiceWorker(
  options: RegistrationOptions = {}
): Promise<ServiceWorkerRegistration | null> {
  if (!('serviceWorker' in navigator)) {
    console.log('[PWA] Service workers not supported');
    return null;
  }

  // Only register in production
  if (import.meta.env.DEV) {
    console.log('[PWA] Skipping service worker registration in development');
    return null;
  }

  try {
    const registration = await navigator.serviceWorker.register('/sw.js', {
      scope: '/',
      updateViaCache: 'none',
    });

    console.log('[PWA] Service Worker registered successfully');

    // Handle updates
    registration.addEventListener('updatefound', () => {
      const installingWorker = registration.installing;
      if (!installingWorker) return;

      installingWorker.addEventListener('statechange', () => {
        if (installingWorker.state === 'installed') {
          if (navigator.serviceWorker.controller) {
            // New update available
            console.log('[PWA] New content available; please refresh');
            options.onUpdate?.(registration);
          } else {
            // Content cached for offline use
            console.log('[PWA] Content cached for offline use');
            options.onSuccess?.(registration);
          }
        }
      });
    });

    // Check for updates every hour
    setInterval(() => {
      registration.update();
    }, 60 * 60 * 1000);

    return registration;
  } catch (error) {
    console.error('[PWA] Service Worker registration failed:', error);
    return null;
  }
}

/**
 * Unregister all service workers
 */
export async function unregisterServiceWorker(): Promise<boolean> {
  if (!('serviceWorker' in navigator)) {
    return false;
  }

  try {
    const registration = await navigator.serviceWorker.ready;
    const success = await registration.unregister();
    console.log('[PWA] Service Worker unregistered:', success);
    return success;
  } catch (error) {
    console.error('[PWA] Error unregistering service worker:', error);
    return false;
  }
}

/**
 * Skip waiting and activate new service worker
 */
export function skipWaiting(): void {
  if (!('serviceWorker' in navigator)) return;

  navigator.serviceWorker.ready.then((registration) => {
    if (registration.waiting) {
      registration.waiting.postMessage({ type: 'SKIP_WAITING' });
    }
  });
}

/**
 * Setup offline/online event listeners
 */
export function setupNetworkListeners(
  onOffline?: () => void,
  onOnline?: () => void
): () => void {
  const handleOffline = () => {
    console.log('[PWA] App is offline');
    onOffline?.();
  };

  const handleOnline = () => {
    console.log('[PWA] App is online');
    onOnline?.();
  };

  window.addEventListener('offline', handleOffline);
  window.addEventListener('online', handleOnline);

  // Return cleanup function
  return () => {
    window.removeEventListener('offline', handleOffline);
    window.removeEventListener('online', handleOnline);
  };
}

/**
 * Check if app is running as installed PWA
 */
export function isInstalledPWA(): boolean {
  // Check for standalone display mode
  if (window.matchMedia('(display-mode: standalone)').matches) {
    return true;
  }

  // iOS check
  if ('standalone' in window.navigator && (window.navigator as any).standalone) {
    return true;
  }

  // Check for minimal-ui (some browsers)
  if (window.matchMedia('(display-mode: minimal-ui)').matches) {
    return true;
  }

  return false;
}

/**
 * Get the install prompt event (stored globally)
 */
let deferredInstallPrompt: BeforeInstallPromptEvent | null = null;

// Extend Window interface for BeforeInstallPromptEvent
interface BeforeInstallPromptEvent extends Event {
  readonly platforms: string[];
  readonly userChoice: Promise<{
    outcome: 'accepted' | 'dismissed';
    platform: string;
  }>;
  prompt(): Promise<void>;
}

declare global {
  interface WindowEventMap {
    beforeinstallprompt: BeforeInstallPromptEvent;
  }
}

/**
 * Initialize install prompt capture
 */
export function initInstallPrompt(): void {
  window.addEventListener('beforeinstallprompt', (e) => {
    // Prevent the mini-infobar from appearing
    e.preventDefault();
    // Store the event for later use
    deferredInstallPrompt = e;
    console.log('[PWA] Install prompt captured');

    // Dispatch custom event for components to react
    window.dispatchEvent(new CustomEvent('pwa-install-available'));
  });

  window.addEventListener('appinstalled', () => {
    console.log('[PWA] App was installed');
    deferredInstallPrompt = null;

    // Dispatch custom event
    window.dispatchEvent(new CustomEvent('pwa-installed'));
  });
}

/**
 * Check if install prompt is available
 */
export function canInstall(): boolean {
  return deferredInstallPrompt !== null;
}

/**
 * Show the install prompt
 */
export async function showInstallPrompt(): Promise<'accepted' | 'dismissed' | 'unavailable'> {
  if (!deferredInstallPrompt) {
    console.log('[PWA] No install prompt available');
    return 'unavailable';
  }

  try {
    // Show the prompt
    await deferredInstallPrompt.prompt();

    // Wait for the user's response
    const { outcome } = await deferredInstallPrompt.userChoice;
    console.log('[PWA] User choice:', outcome);

    // Clear the prompt
    deferredInstallPrompt = null;

    return outcome;
  } catch (error) {
    console.error('[PWA] Error showing install prompt:', error);
    return 'unavailable';
  }
}

/**
 * Check browser support for various PWA features
 */
export interface PWASupport {
  serviceWorker: boolean;
  pushNotifications: boolean;
  backgroundSync: boolean;
  periodicSync: boolean;
  offlineStorage: boolean;
  shareTarget: boolean;
  webShare: boolean;
  mediaSession: boolean;
  badging: boolean;
}

export function checkPWASupport(): PWASupport {
  return {
    serviceWorker: 'serviceWorker' in navigator,
    pushNotifications: 'PushManager' in window,
    backgroundSync: 'serviceWorker' in navigator && 'SyncManager' in window,
    periodicSync: 'serviceWorker' in navigator && 'PeriodicSyncManager' in window,
    offlineStorage: 'caches' in window,
    shareTarget: 'share' in navigator,
    webShare: 'share' in navigator,
    mediaSession: 'mediaSession' in navigator,
    badging: 'setAppBadge' in navigator,
  };
}

/**
 * Set app badge (for notification count)
 */
export async function setAppBadge(count: number): Promise<void> {
  if ('setAppBadge' in navigator) {
    try {
      if (count > 0) {
        await (navigator as any).setAppBadge(count);
      } else {
        await (navigator as any).clearAppBadge();
      }
    } catch (error) {
      console.error('[PWA] Error setting app badge:', error);
    }
  }
}

/**
 * Clear app badge
 */
export async function clearAppBadge(): Promise<void> {
  if ('clearAppBadge' in navigator) {
    try {
      await (navigator as any).clearAppBadge();
    } catch (error) {
      console.error('[PWA] Error clearing app badge:', error);
    }
  }
}

/**
 * Share content using Web Share API
 */
export async function shareContent(data: {
  title?: string;
  text?: string;
  url?: string;
}): Promise<boolean> {
  if (!('share' in navigator)) {
    console.log('[PWA] Web Share API not supported');
    return false;
  }

  try {
    await navigator.share(data);
    return true;
  } catch (error) {
    if ((error as Error).name !== 'AbortError') {
      console.error('[PWA] Error sharing:', error);
    }
    return false;
  }
}

/**
 * Request persistent storage
 */
export async function requestPersistentStorage(): Promise<boolean> {
  if (!('storage' in navigator && 'persist' in navigator.storage)) {
    return false;
  }

  try {
    const persisted = await navigator.storage.persist();
    console.log('[PWA] Persistent storage:', persisted ? 'granted' : 'denied');
    return persisted;
  } catch (error) {
    console.error('[PWA] Error requesting persistent storage:', error);
    return false;
  }
}

/**
 * Check storage quota and usage
 */
export async function getStorageEstimate(): Promise<{
  usage: number;
  quota: number;
  percentUsed: number;
} | null> {
  if (!('storage' in navigator && 'estimate' in navigator.storage)) {
    return null;
  }

  try {
    const estimate = await navigator.storage.estimate();
    const usage = estimate.usage || 0;
    const quota = estimate.quota || 0;
    const percentUsed = quota > 0 ? (usage / quota) * 100 : 0;

    return { usage, quota, percentUsed };
  } catch (error) {
    console.error('[PWA] Error getting storage estimate:', error);
    return null;
  }
}
